import { StyleSheet, css } from 'aphrodite'
import type React from 'react'
import { useRef, useState } from 'react'
import { ReactMultiEmail, isEmail } from 'react-multi-email'
import SignaturePad from 'react-signature-canvas'
import type { User } from '../types/user'
import UserSelect from './UserSelect'
import 'react-multi-email/dist/style.css'

interface CustomSupervisor {
  name: string
  email: string
  phone?: string
}

interface SignatureFormProps {
  confirmationText?: string
  // For site supervisor selection - now required
  selectedSiteSupervisor: User | null
  projectSiteSupervisors: User[]
  onSiteSupervisorChange: (supervisor: User | null) => void
  // New props for custom supervisor
  customSupervisor: CustomSupervisor | null
  onCustomSupervisorChange: (supervisor: CustomSupervisor | null) => void
  supervisorSelectionMode: 'assigned' | 'custom'
  onSupervisorModeChange: (mode: 'assigned' | 'custom') => void
  onSubmit: (data: {
    supervisorSignature: string
    driverSignature: string
    sharedEmails: string[]
    selectedSiteSupervisor: User | null
    customSupervisor: CustomSupervisor | null
    supervisorSelectionMode: 'assigned' | 'custom'
  }) => void
  submitButtonText?: string
  isValid: (data: {
    supervisorSignature: string
    driverSignature: string
    selectedSiteSupervisor: User | null
    customSupervisor: CustomSupervisor | null
    supervisorSelectionMode: 'assigned' | 'custom'
  }) => boolean
  requireBothSignatures?: boolean
}

const SignatureForm: React.FC<SignatureFormProps> = ({
  confirmationText,
  selectedSiteSupervisor,
  projectSiteSupervisors,
  onSiteSupervisorChange,
  customSupervisor,
  onCustomSupervisorChange,
  supervisorSelectionMode,
  onSupervisorModeChange,
  onSubmit,
  submitButtonText,
  isValid,
  requireBothSignatures = false,
}) => {
  const supervisorSignaturePadRef = useRef<SignaturePad | null>(null)
  const driverSignaturePadRef = useRef<SignaturePad | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [sharedEmails, setSharedEmails] = useState<string[]>([])
  const [hasSupervisorSignature, setHasSupervisorSignature] = useState(false)
  const [hasDriverSignature, setHasDriverSignature] = useState(false)

  const handleSubmit = async (e: React.MouseEvent) => {
    e.preventDefault()

    // Check if signatures are present before setting submitting state
    if (
      !supervisorSignaturePadRef.current ||
      supervisorSignaturePadRef.current.isEmpty()
    ) {
      console.error('Supervisor signature is required')
      return
    }
    if (
      !driverSignaturePadRef.current ||
      driverSignaturePadRef.current.isEmpty()
    ) {
      console.error('Driver signature is required')
      return
    }

    setIsSubmitting(true)
    try {
      const supervisorSignatureImage = supervisorSignaturePadRef.current
        .getTrimmedCanvas()
        .toDataURL('image/png')
      const driverSignatureImage = driverSignaturePadRef.current
        .getTrimmedCanvas()
        .toDataURL('image/png')

      await onSubmit({
        supervisorSignature: supervisorSignatureImage,
        driverSignature: driverSignatureImage,
        sharedEmails: sharedEmails,
        selectedSiteSupervisor,
        customSupervisor,
        supervisorSelectionMode,
      })
    } catch (error) {
      console.error('Error submitting form:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className={css(styles.container)}>
      <div className={css(styles.mainContent)}>
        <div className={css(styles.contentSection)}>
          {/* Site Supervisor Selection - Required */}
          <div className={css(styles.inputGroup)}>
            <label className={css(styles.label)}>Site Supervisor *</label>

            {/* Supervisor Selection Mode Toggle */}
            <div className={css(styles.radioGroup)}>
              <label className={css(styles.radioLabel)}>
                <input
                  type="radio"
                  name="supervisorMode"
                  value="assigned"
                  checked={supervisorSelectionMode === 'assigned'}
                  onChange={(e) =>
                    onSupervisorModeChange(
                      e.target.value as 'assigned' | 'custom',
                    )
                  }
                  className={css(styles.radioInput)}
                />
                <span className={css(styles.radioText)}>
                  Select from assigned supervisors
                </span>
              </label>
              <label className={css(styles.radioLabel)}>
                <input
                  type="radio"
                  name="supervisorMode"
                  value="custom"
                  checked={supervisorSelectionMode === 'custom'}
                  onChange={(e) =>
                    onSupervisorModeChange(
                      e.target.value as 'assigned' | 'custom',
                    )
                  }
                  className={css(styles.radioInput)}
                />
                <span className={css(styles.radioText)}>
                  Enter custom supervisor
                </span>
              </label>
            </div>

            {/* Assigned Supervisor Selection */}
            {supervisorSelectionMode === 'assigned' && (
              <UserSelect
                label=""
                selectedUser={selectedSiteSupervisor}
                users={projectSiteSupervisors}
                onUserChange={onSiteSupervisorChange}
                placeholder="Select a site supervisor"
              />
            )}

            {/* Custom Supervisor Form */}
            {supervisorSelectionMode === 'custom' && (
              <div className={css(styles.customSupervisorForm)}>
                <div className={css(styles.formRow)}>
                  <div className={css(styles.formField)}>
                    <label className={css(styles.fieldLabel)}>Name *</label>
                    <input
                      type="text"
                      value={customSupervisor?.name || ''}
                      onChange={(e) =>
                        onCustomSupervisorChange({
                          ...customSupervisor,
                          name: e.target.value,
                          email: customSupervisor?.email || '',
                          phone: customSupervisor?.phone || '',
                        })
                      }
                      placeholder="Enter supervisor name"
                      className={css(styles.textInput)}
                    />
                  </div>
                  <div className={css(styles.formField)}>
                    <label className={css(styles.fieldLabel)}>Email *</label>
                    <input
                      type="email"
                      value={customSupervisor?.email || ''}
                      onChange={(e) =>
                        onCustomSupervisorChange({
                          ...customSupervisor,
                          name: customSupervisor?.name || '',
                          email: e.target.value,
                          phone: customSupervisor?.phone || '',
                        })
                      }
                      placeholder="Enter supervisor email"
                      className={css(styles.textInput)}
                    />
                  </div>
                </div>
                <div className={css(styles.formRow)}>
                  <div className={css(styles.formField)}>
                    <label className={css(styles.fieldLabel)}>
                      Phone (Optional)
                    </label>
                    <input
                      type="tel"
                      value={customSupervisor?.phone || ''}
                      onChange={(e) =>
                        onCustomSupervisorChange({
                          ...customSupervisor,
                          name: customSupervisor?.name || '',
                          email: customSupervisor?.email || '',
                          phone: e.target.value,
                        })
                      }
                      placeholder="Enter supervisor phone"
                      className={css(styles.textInput)}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className={css(styles.inputGroup)}>
            <label className={css(styles.label)}>
              Additional Emails (Optional)
            </label>
            <ReactMultiEmail
              placeholder="Enter email addresses"
              emails={sharedEmails}
              onChange={(emails: string[]) => {
                setSharedEmails(emails)
              }}
              validateEmail={(email) => {
                return isEmail(email)
              }}
              getLabel={(
                email: string,
                index: number,
                removeEmail: (index: number) => void,
              ) => {
                return (
                  <div data-tag key={index}>
                    {email}
                    <button
                      data-tag-handle
                      onClick={() => removeEmail(index)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          removeEmail(index)
                        }
                      }}
                    >
                      ×
                    </button>
                  </div>
                )
              }}
            />
          </div>
        </div>
      </div>

      <div className={css(styles.bottomSection)}>
        <div className={css(styles.confirmationTextContainer)}>
          <p className={css(styles.confirmationText)}>{confirmationText}</p>
        </div>

        <div className={css(styles.signatureBox)}>
          <div className={css(styles.signatureSection)}>
            <p className={css(styles.signatureLabel)}>
              Site Supervisor Signature
            </p>
            <SignaturePad
              ref={supervisorSignaturePadRef}
              onEnd={() => setHasSupervisorSignature(true)}
              canvasProps={{
                className: css(styles.canvas),
                width: 358,
                height: 200,
              }}
            />
            <div className={css(styles.clearButtonContainer)}>
              <button
                onClick={() => {
                  supervisorSignaturePadRef.current?.clear()
                  setHasSupervisorSignature(false)
                }}
                className={css(styles.clearButton)}
              >
                Clear
              </button>
            </div>
          </div>
        </div>

        <div className={css(styles.confirmationTextContainer)}>
          <p className={css(styles.confirmationText)}>
            I confirm loads have been delivered:{' '}
          </p>
        </div>

        <div className={css(styles.signatureBox)}>
          <div className={css(styles.signatureSection)}>
            <p className={css(styles.signatureLabel)}>Driver Signature</p>
            <SignaturePad
              ref={driverSignaturePadRef}
              onEnd={() => setHasDriverSignature(true)}
              canvasProps={{
                className: css(styles.canvas),
                width: 358,
                height: 200,
              }}
            />
            <div className={css(styles.clearButtonContainer)}>
              <button
                onClick={() => {
                  driverSignaturePadRef.current?.clear()
                  setHasDriverSignature(false)
                }}
                className={css(styles.clearButton)}
              >
                Clear
              </button>
            </div>
          </div>
        </div>

        <div className={css(styles.submitButtonContainer)}>
          <button
            type="button"
            className={css(styles.submitButton)}
            onClick={handleSubmit}
            disabled={
              !hasSupervisorSignature || !hasDriverSignature || isSubmitting
            }
          >
            {isSubmitting && (
              <i
                className="fa-solid fa-spinner fa-spin"
                style={{ marginRight: '8px' }}
              />
            )}
            {submitButtonText}
          </button>
        </div>
        <div className={css(styles.bottomSpacer)} />
      </div>
    </div>
  )
}

const styles = StyleSheet.create({
  container: {
    alignItems: 'center',
    margin: '0 auto',
    maxWidth: '390px',
  },
  mainContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    padding: 16,
    gap: 8,
    width: '100%',
  },
  contentSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    width: '100%',
  },
  confirmationTextContainer: {
    padding: '0 4px',
    alignSelf: 'flex-start',
  },
  confirmationText: {
    fontFamily: 'Inter, sans-serif',
    fontWeight: 500,
    fontSize: 14,
    lineHeight: '150%',
    color: '#111928',
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    width: '100%',
  },
  label: {
    fontFamily: 'Inter, sans-serif',
    fontWeight: 500,
    fontSize: 14,
    lineHeight: '150%',
    color: '#111928',
  },
  input: {
    boxSizing: 'border-box',
    width: '100%',
    height: '48px',
    padding: '14px 20px',
    backgroundColor: '#F9FAFB',
    border: '1px solid #E5E7EB',
    borderRadius: 8,
    fontFamily: 'Inter, sans-serif',
    fontSize: 16,
    lineHeight: '150%',
    color: '#111928',
    paddingLeft: 38,
    '& .react-multi-email': {
      backgroundColor: 'transparent !important',
      border: 'none !important',
      padding: '0 !important',
      boxShadow: 'none !important',
    },
    '& input': {
      backgroundColor: 'transparent !important',
    },
  },
  bottomSection: {
    display: 'flex',
    flexDirection: 'column',
    padding: 16,
    gap: 16,
    width: '100%',
    alignItems: 'center',
  },
  buttonGroupContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    width: '100%',
  },
  buttonGroup: {
    display: 'flex',
    flexDirection: 'row',
    gap: 10,
    width: '100%',
  },
  cancelButton: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '12px 20px',
    width: 80,
    border: '1px solid #1C64F2',
    borderRadius: 8,
    fontFamily: 'Inter, sans-serif',
    fontWeight: 500,
    fontSize: 16,
    lineHeight: '150%',
    color: '#1C64F2',
    cursor: 'pointer',
    backgroundColor: '#fff',
  },
  submitButtonContainer: {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
    maxWidth: '800px',
    margin: '0 auto',
  },
  submitButton: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '12px 20px',
    backgroundColor: '#10B981',
    border: 'none',
    borderRadius: 8,
    fontFamily: 'Inter, sans-serif',
    fontWeight: 500,
    fontSize: 16,
    lineHeight: '150%',
    color: '#FFFFFF',
    cursor: 'pointer',
    width: '100%',
    margin: '16px',
    ':disabled': {
      backgroundColor: '#E5E7EB',
      cursor: 'not-allowed',
      color: '#9CA3AF',
    },
  },
  signatureBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    backgroundColor: '#FFFFFF',
    border: '1px solid #E5E7EB',
    borderRadius: 8,
    overflow: 'hidden',
  },
  canvas: {
    border: '1px solid #E5E7EB',
    borderRadius: 8,
    touchAction: 'none',
    marginTop: 8,
    width: 'calc(100% - 1px)',
  },
  signatureText: {
    fontFamily: 'Inter, sans-serif',
    fontSize: 14,
    color: '#6B7280',
    marginTop: 8,
  },
  inputWrapper: {
    position: 'relative',
    width: '100%',
  },
  inputIcon: {
    position: 'absolute',
    left: 14,
    top: '50%',
    transform: 'translateY(-50%)',
    zIndex: 2,
    color: 'rgb(156, 163, 175)',
  },
  phoneInputContainer: {
    width: '100%',
  },
  phoneInputField: {
    width: '100%',
    height: '48px !important',
    borderRadius: '8px !important',
    border: '1px solid #E5E7EB !important',
    fontSize: '16px !important',
    backgroundColor: '#F9FAFB !important',
    paddingLeft: '38px !important',
    ':focus': {
      outline: 'none',
      borderColor: '#2563EB',
      boxShadow: '0 0 0 1px #2563EB',
    },
  },
  clearButtonContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
    padding: '8px 16px',
  },
  clearButton: {
    border: 'none',
    background: 'none',
    color: '#6B7280',
    cursor: 'pointer',
  },
  bottomSpacer: {
    height: 40,
    width: '100%',
  },
  signatureSection: {
    width: '100%',
    padding: '16px',
    borderBottom: '1px solid #E5E7EB',
  },
  signatureLabel: {
    fontSize: '14px',
    fontWeight: 500,
    color: '#374151',
    marginBottom: '8px',
  },
  // New styles for supervisor selection
  radioGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    marginBottom: 16,
  },
  radioLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    cursor: 'pointer',
    fontFamily: 'Inter, sans-serif',
    fontSize: 14,
    color: '#111928',
  },
  radioInput: {
    margin: 0,
    cursor: 'pointer',
  },
  radioText: {
    fontFamily: 'Inter, sans-serif',
    fontSize: 14,
    color: '#111928',
  },
  customSupervisorForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    padding: 16,
    backgroundColor: '#F9FAFB',
    border: '1px solid #E5E7EB',
    borderRadius: 8,
  },
  formRow: {
    display: 'flex',
    gap: 12,
    width: '100%',
  },
  formField: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    flex: 1,
  },
  fieldLabel: {
    fontFamily: 'Inter, sans-serif',
    fontWeight: 500,
    fontSize: 12,
    color: '#374151',
  },
  textInput: {
    width: '100%',
    height: 40,
    padding: '8px 12px',
    backgroundColor: '#FFFFFF',
    border: '1px solid #D1D5DB',
    borderRadius: 6,
    fontFamily: 'Inter, sans-serif',
    fontSize: 14,
    color: '#111928',
    ':focus': {
      outline: 'none',
      borderColor: '#2563EB',
      boxShadow: '0 0 0 1px #2563EB',
    },
  },
})

export default SignatureForm
